import React from 'react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { courses_api, courseDelete, cancelApprovalCourse, sendApprovalCourse } from '../../../services/adminService';
import DataTable from '../../../components/admin/DataTable';
import CourseThumbnail from '../../../components/admin/CourseThumbnail';
import { encodeData } from '../../../utils/encodeAndEncode';
import { usePermissions } from '../../../context/PermissionsContext';
import CourseAssignModal from './CourseAssignModal';

const tooltipStyles = `
  .action-btn-tooltip {
    position: relative;
  }
  .action-btn-tooltip:hover::after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    padding: 4px 8px;
    background: #1f2937;
    color: white;
    font-size: 11px;
    white-space: nowrap;
    border-radius: 4px;
    margin-bottom: 4px;
    z-index: 1000;
  }
  .action-btn-tooltip:hover::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: #1f2937;
    z-index: 1000;
  }
`;

function AllCourses({ searchTerm = '', setSearchTerm }) {
    const navigate = useNavigate();
    const { permissions } = usePermissions();
    const [courseList, setCourseList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10);
    const [internalSearch, setInternalSearch] = useState('');
    const search = setSearchTerm != null ? (searchTerm ?? '') : internalSearch;
    const setSearch = setSearchTerm ?? setInternalSearch;
    const [statusFilter, setStatusFilter] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [assignCourse, setAssignCourse] = useState(null);

    const minLoadTime = 500;

    const fetchCourseList = async () => {
        const startTime = Date.now();
        try {
            const response = await courses_api();
            if (response.success === true) {
                setCourseList(response.data.all || []);
            } else {
                setError(response.message);
            }
        } catch (err) {
            setError(err.message);
        } finally {
            const elapsed = Date.now() - startTime;
            const remaining = Math.max(0, minLoadTime - elapsed);
            setTimeout(() => setLoading(false), remaining);
        }
    };

    useEffect(() => {
        setLoading(true);
        fetchCourseList();
    }, []);

    // Filter courses based on search term and status
    const filteredCourses = courseList.filter(course => {
        const matchesSearch = course.course_name.toLowerCase().includes(search.toLowerCase());
        const matchesStatus = !statusFilter || course.course_status === statusFilter;
        return matchesSearch && matchesStatus;
    });

    // Calculate pagination
    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    const currentRecords = filteredCourses.slice(indexOfFirstRecord, indexOfLastRecord);
    const totalPages = Math.ceil(filteredCourses.length / recordsPerPage);

    const handleNavigateToModule = (courseId) => {
        if (!permissions.course_module_management_view) {
            toast.warning("You don't have permission to view course modules");
            return;
        }
        const encodedCourseId = encodeData(courseId);
        navigate(`/admin/courses/module/${encodedCourseId}`);
    };

    const handleNavigateToAnalytics = (e, courseId) => {
        e.stopPropagation();
        if (!permissions.course_management_view_analytics) {
            toast.warning("You don't have permission to view analytics");
            return;
        }
        const encodedCourseId = encodeData(courseId);
        navigate(`/admin/courses/analytics/${encodedCourseId}`);
    };

    const handleEditCourse = (e, course) => {
        e.stopPropagation();
        if (!permissions.course_management_edit) {
            toast.warning("You don't have permission to edit courses");
            return;
        }
        navigate(`/admin/courses/edit-create/${encodeData(course.id)}`);
    };

    const formatStatus = (status) => (status || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

    const getStatusClass = (status) => {
        const statusClasses = {
            'approved': 'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-50 border border-green-300 text-green-600 p-2',
            'pending': 'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-50 border border-yellow-300 text-yellow-600 p-2',
            'rejected': 'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-50 border border-red-300 text-red-600 p-2',
            'waiting_for_approval': 'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-50 border border-blue-300 text-blue-600 p-2'
        }
        return statusClasses[status] || 'inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-50 border border-gray-300 text-gray-600 p-2'
    }

    const handleDeleteClick = (e, course) => {
        e.stopPropagation();
        if (!permissions.course_management_delete) {
            toast.warning("You don't have permission to delete courses");
            return;
        }
        setSelectedCourse(course);
        setShowDeleteModal(true);
    };

    const handleCloseModal = () => {
        setShowDeleteModal(false);
        setSelectedCourse(null);
    };

    const handleDeleteConfirm = async () => {
        try {
            const response = await courseDelete({ course_id: selectedCourse.id });
            if (response.success === true) {
                toast.success('Course deleted successfully', {
                    position: 'top-center',
                    autoClose: 3000
                });
                setCourseList(prevList => prevList.filter(course => course.id !== selectedCourse.id));
            } else {
                toast.error(response.message || 'Failed to delete course', {
                    position: 'top-center',
                    autoClose: 3000
                });
            }
        } catch (error) {
            toast.error('Error deleting course: ' + error.message, {
                position: 'top-center',
                autoClose: 3000
            });
        } finally {
            handleCloseModal();
        }
    };

    const handleCancelClick = (e, course) => {
        e.stopPropagation();
        if (!permissions.course_management_cancel_approval) {
            toast.warning("You don't have permission to cancel approval requests");
            return;
        }
        setSelectedCourse(course);
        setShowCancelModal(true);
    };

    const handleCloseCancelModal = () => {
        setShowCancelModal(false);
        setSelectedCourse(null);
    };

    const handleCancelConfirm = async () => {
        try {
            const response = await cancelApprovalCourse({ course_id: selectedCourse.id });

            if (response.success === true) {
                toast.success('Approval request cancelled successfully', {
                    position: 'top-center',
                    autoClose: 3000
                });
                // Refresh the course list
                fetchCourseList();
            } else {
                toast.error(response.message || 'Failed to cancel approval request', {
                    position: 'top-center',
                    autoClose: 3000
                });
            }
        } catch (error) {
            toast.error('Error cancelling approval request: ' + error.message, {
                position: 'top-center',
                autoClose: 3000
            });
        } finally {
            handleCloseCancelModal();
        }
    };

    const handleSendApprovalClick = (e, course) => {
        e.stopPropagation();
        if (course.course_status === 'pending') {
            if (!permissions.course_management_send_approval) {
                toast.warning("You don't have permission to send approval requests");
                return;
            }
        } else {
            if (!permissions.course_management_resend_approval) {
                toast.warning("You don't have permission to resend approval requests");
                return;
            }
        }
        setSelectedCourse(course);
        setShowApprovalModal(true);
    };

    const handleCloseApprovalModal = () => {
        setShowApprovalModal(false);
        setSelectedCourse(null);
    };

    const handleSendApprovalConfirm = async () => {
        try {
            const user_id = JSON.parse(localStorage.getItem('user'))?.id;
            const response = await sendApprovalCourse({ 
                user_id: user_id,
                course_id: selectedCourse.id 
            });

            if (response.success === true) {
                toast.success(selectedCourse?.course_status === 'pending' ? 'Course sent for approval successfully' : 'Course resent for approval successfully', {
                    position: 'top-center',
                    autoClose: 3000
                });
                // Refresh the course list
                fetchCourseList();
            } else {
                toast.error(response.message || 'Failed to send course for approval', {
                    position: 'top-center',
                    autoClose: 3000
                });
            }
        } catch (error) {
            toast.error('Error sending course for approval: ' + error.message, {
                position: 'top-center',
                autoClose: 3000
            });
        } finally {
            handleCloseApprovalModal();
        }
    };

    return (
        <>
            <style>{tooltipStyles}</style>
            {/* Table */}
            <DataTable
              loading={loading}
              hasRows={currentRecords.length > 0}
              caption="Loading Courses..."
              minHeight="400px"
            >
              <table className="min-w-full text-sm" style={{ minWidth: '800px' }}>
                      <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                        <tr>
                          <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-[40%]">Course</th>
                          <th className="px-4 py-3.5 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-28">Enrolled</th>
                          {/* <th className="px-4 py-3.5 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-28">Price</th> */}
                          <th className="px-4 py-3.5 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-32">Rating</th>
                          <th className="px-4 py-3.5 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-28">Status</th>
                          <th className="px-4 py-3.5 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-40 min-w-[280px]">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {currentRecords.map((course) => (
                          <tr
                            key={course.id}
                            className="hover:bg-gray-50 transition-colors cursor-pointer"
                            onClick={() => handleNavigateToModule(course.id)}
                          >
                            {/* Course info */}
                            <td className="px-4 py-3 align-middle">
                              <div className="flex items-center gap-3">
                                <CourseThumbnail
                                  src={course.banner_image}
                                  alt={course.course_name}
                                />
                                <div className="min-w-0">
                                  <div className="font-medium text-gray-900 mb-1 line-clamp-2">
                                    {course.course_name}
                                  </div>
                                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                                    <span className="inline-flex items-center gap-1">
                                      <Icon icon="tabler:video" width="14" height="14" />
                                      <span>{course.course_summary.total_videos} Videos</span>
                                    </span>
                                    <span className="inline-flex items-center gap-1">
                                      <Icon icon="tabler:file-text" width="14" height="14" />
                                      <span>{course.course_summary.total_documents} Docs</span>
                                    </span>
                                    <span className="inline-flex items-center gap-1">
                                      <Icon icon="tabler:writing" width="14" height="14" />
                                      <span>{course.course_summary.total_assessments} Assessments</span>
                                    </span>
                                    <span className="inline-flex items-center gap-1">
                                      <Icon icon="tabler:chart-dots" width="14" height="14" />
                                      <span>{course.course_summary.total_surveys} Surveys</span>
                                    </span>
                                    <span className="inline-flex items-center gap-1">
                                      <Icon icon="tabler:clock" width="14" height="14" />
                                      <span>{course.course_summary.total_duration?.split('.')[0] || '00:00:00'}</span>
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* Enrolled */}
                            <td className="px-4 py-3 align-middle text-center text-gray-800">
                              {course.course_summary.total_purchased || 0}
                            </td>

                            {/* Price - hidden */}
                            {/* <td className="px-4 py-3 align-middle text-center">
                              {course.course_price
                                ? <span className="inline-flex items-center justify-center px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs font-medium">
                                    {course.course_price}
                                  </span>
                                : <span className="inline-flex items-center justify-center px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium">
                                    Free
                                  </span>}
                            </td> */}

                            {/* Rating */}
                            <td className="px-4 py-3 align-middle text-center">
                              <div className="inline-flex items-center justify-center gap-1 px-2 py-1 rounded-full bg-amber-50 text-amber-700 text-xs">
                                <Icon icon="fluent:star-12-filled" width="14" height="14" />
                                <span>{course.course_summary.average_rating || '0.0'}</span>
                              </div>
                            </td>

                            {/* Status */}
                            <td className="px-4 py-3 align-middle text-center">
                              <span className={`${getStatusClass(course.course_status)} text-xs font-medium px-3 py-1 rounded-full inline-flex items-center justify-center`}>
                                {course.course_status === 'rejected' ? 'Delisted' : course.course_status === 'waiting_for_approval' ? 'Waiting' : formatStatus(course.course_status)}
                              </span>
                            </td>

                            {/* Actions */}
                            <td className="px-4 py-3 align-middle whitespace-nowrap">
                              <div className="flex items-center justify-center gap-2 flex-nowrap shrink-0" onClick={(e) => e.stopPropagation()}>
                                {course.course_status === 'approved' && (
                                  <button
                                    type="button"
                                    className="action-btn-tooltip inline-flex items-center justify-center w-9 h-9 rounded-lg bg-green-500 text-white text-xs shadow-sm cursor-not-allowed"
                                    data-tooltip="Course Approved"
                                  >
                                    <Icon icon="fluent:checkmark-24-filled" width="16" height="16" />
                                  </button>
                                )}

                                {course.course_status === 'rejected' && (
                                  <>
                                    <button
                                      type="button"
                                      className="action-btn-tooltip inline-flex items-center justify-center w-9 h-9 rounded-lg border border-blue-500 text-blue-600 hover:bg-blue-50 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                                      data-tooltip={!permissions.course_management_edit ? "No permission" : 'Edit Course'}
                                      onClick={(e) => handleEditCourse(e, course)}
                                      disabled={!permissions.course_management_edit}
                                    >
                                      <Icon icon="fluent:edit-24-regular" width="16" height="16" />
                                    </button>
                                    <button
                                      type="button"
                                      className="action-btn-tooltip inline-flex items-center justify-center w-9 h-9 rounded-lg border border-green-500 text-green-600 hover:bg-green-50 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                                      data-tooltip={!permissions.course_management_resend_approval ? "No permission" : 'Resend Approval'}
                                      onClick={(e) => handleSendApprovalClick(e, course)}
                                      disabled={!permissions.course_management_resend_approval}
                                    >
                                      <Icon icon="fluent:arrow-repeat-all-24-regular" width="16" height="16" />
                                    </button>
                                    <button
                                      type="button"
                                      className="action-btn-tooltip inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                                      data-tooltip={!permissions.course_management_cancel_approval ? "No permission" : 'Cancel Approval'}
                                      onClick={(e) => handleCancelClick(e, course)}
                                      disabled={!permissions.course_management_cancel_approval}
                                    >
                                      <Icon icon="fluent:dismiss-24-regular" width="16" height="16" />
                                    </button>
                                  </>
                                )}

                                {course.course_status !== 'waiting_for_approval' && course.course_status !== 'rejected' && (
                                  <>
                                    <button
                                      type="button"
                                      className="action-btn-tooltip inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                                      data-tooltip={
                                        course.course_status === 'approved'
                                          ? 'Cannot edit'
                                          : !permissions.course_management_edit
                                            ? "No permission"
                                            : 'Edit Course'
                                      }
                                      onClick={(e) => handleEditCourse(e, course)}
                                      disabled={course.course_status === 'approved' || !permissions.course_management_edit}
                                    >
                                      <Icon icon="fluent:edit-24-regular" width="16" height="16" />
                                    </button>
                                    {course.course_status === 'pending' && (
                                      <button
                                        type="button"
                                        className="action-btn-tooltip inline-flex items-center justify-center w-9 h-9 rounded-lg border border-green-500 text-green-600 hover:bg-green-50 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                                        data-tooltip={!permissions.course_management_resend_approval ? "No permission" : 'Send Approval'}
                                        onClick={(e) => handleSendApprovalClick(e, course)}
                                        disabled={!permissions.course_management_resend_approval}
                                      >
                                        <Icon icon="fluent:send-24-regular" width="16" height="16" />
                                      </button>
                                    )}
                                  </>
                                )}

                                {course.course_status === 'waiting_for_approval' && (
                                  <button
                                    type="button"
                                    className="action-btn-tooltip inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                                    data-tooltip={!permissions.course_management_cancel_approval ? "No permission" : 'Cancel Approval'}
                                    onClick={(e) => handleCancelClick(e, course)}
                                    disabled={!permissions.course_management_cancel_approval}
                                  >
                                    <Icon icon="fluent:dismiss-24-regular" width="16" height="16" />
                                  </button>
                                )}

                                <button
                                  type="button"
                                  className="action-btn-tooltip inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                                  data-tooltip={
                                    course.course_status === 'approved'
                                      ? 'Cannot delete'
                                      : !permissions.course_management_delete
                                        ? "No permission"
                                        : 'Delete Course'
                                  }
                                  onClick={(e) => handleDeleteClick(e, course)}
                                  disabled={course.course_status === 'approved' || !permissions.course_management_delete}
                                >
                                  <Icon icon="fluent:delete-24-regular" width="16" height="16" />
                                </button>

                                <button
                                  type="button"
                                  className="action-btn-tooltip inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                                  data-tooltip={!permissions.course_module_management_view ? "No permission" : 'View Modules'}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleNavigateToModule(course.id);
                                  }}
                                  disabled={!permissions.course_module_management_view}
                                >
                                  <Icon icon="fluent:book-24-regular" width="16" height="16" />
                                </button>

                                <button
                                  type="button"
                                  className="action-btn-tooltip inline-flex items-center justify-center w-9 h-9 rounded-lg border border-[#7083FF] text-[#7083FF] hover:bg-[#7083FF]/10 text-xs"
                                  data-tooltip="Assign Users"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setAssignCourse(course);
                                  }}
                                >
                                  <Icon icon="fluent:person-add-24-regular" width="16" height="16" />
                                </button>

                                <button
                                  type="button"
                                  className="action-btn-tooltip inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                                  data-tooltip={!permissions.course_management_view_analytics ? "No permission" : 'Analytics'}
                                  onClick={(e) => handleNavigateToAnalytics(e, course.id)}
                                  disabled={!permissions.course_management_view_analytics}
                                >
                                  <Icon icon="fluent:chart-multiple-24-regular" width="16" height="16" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
            </DataTable>

            {/* Record per page and pagination */}
            <div className="mt-6 pt-4 border-t border-gray-100 flex flex-col md:flex-row items-center justify-between gap-4">
              {/* Rows per page + summary */}
              <div className="flex items-center gap-3 text-sm text-gray-600">
                <span className="hidden sm:inline">Rows per page:</span>
                <select
                  className="px-3 py-1.5 border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm"
                  value={recordsPerPage}
                  onChange={(e) => {
                    setRecordsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-gray-500">
                  {filteredCourses.length === 0
                    ? 'No records'
                    : `Showing ${(currentPage - 1) * recordsPerPage + 1}–${Math.min(
                        currentPage * recordsPerPage,
                        filteredCourses.length
                      )} of ${filteredCourses.length}`}
                </span>
              </div>

              {/* Pagination controls */}
              <div className="flex items-center gap-2 text-sm">
                <button
                  type="button"
                  className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1 || totalPages === 0}
                  title="Previous page"
                >
                  <Icon icon="fluent:chevron-left-24-regular" width="16" height="16" />
                </button>
                <span className="px-2 text-gray-700">
                  Page <span className="font-medium">{totalPages === 0 ? 0 : currentPage}</span> of{' '}
                  <span className="font-medium">{totalPages || 0}</span>
                </span>
                <button
                  type="button"
                  className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages || totalPages === 0}
                  title="Next page"
                >
                  <Icon icon="fluent:chevron-right-24-regular" width="16" height="16" />
                </button>
              </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                            <h5 className="text-lg font-semibold">Confirm Delete</h5>
                            <button 
                                type="button" 
                                className="text-gray-400 hover:text-gray-600"
                                onClick={handleCloseModal}
                            >
                                <Icon icon="mdi:close" width="24" height="24" />
                            </button>
                        </div>
                        <div className="p-6">
                            <p>Are you sure you want to delete the course "{selectedCourse?.course_name}"?</p>
                            <p className="text-red-500 mb-0">This action cannot be undone.</p>
                        </div>
                        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
                            <button 
                                type="button" 
                                className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg" 
                                onClick={handleCloseModal}
                            >
                                Cancel
                            </button>
                            <button 
                                type="button" 
                                className="bg-red-500 hover:bg-red-600 text-white font-medium py-2 px-4 rounded-lg"
                                onClick={handleDeleteConfirm}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Cancel Approval Modal */}
            {showCancelModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                            <h5 className="text-lg font-semibold">Cancel Approval Request</h5>
                            <button 
                                type="button" 
                                className="text-gray-400 hover:text-gray-600"
                                onClick={handleCloseCancelModal}
                            >
                                <Icon icon="mdi:close" width="24" height="24" />
                            </button>
                        </div>
                        <div className="p-6">
                            <p>Are you sure you want to cancel the approval request for "{selectedCourse?.course_name}"?</p>
                            <p>After cancelling:</p>
                            <ul className="list-disc list-inside">
                                <li>The course will return to pending status</li>
                                <li>You can edit the course details again</li>
                                <li>You'll need to submit for approval again when ready</li>
                            </ul>
                        </div>
                        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2">
                            <button
                                type="button"
                                className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-lg"
                                onClick={handleCloseCancelModal}
                            >
                                Close
                            </button>
                            <button
                                type="button"
                                className="bg-yellow-500 hover:bg-yellow-600 text-white font-medium py-2 px-4 rounded-lg"
                                onClick={handleCancelConfirm}
                            >
                                Cancel Approval
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Send Approval Modal */}
            {showApprovalModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
                        {/* Header */}
                        <div className="px-6 py-5 bg-gradient-to-r from-indigo-50 to-blue-50 border-b border-gray-100">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <h5 className="text-lg font-semibold text-gray-900">{selectedCourse?.course_status === 'pending' ? 'Send Course for Approval' : 'Resend Course for Approval'}</h5>
                                </div>
                                <button
                                    type="button"
                                    className="w-8 h-8 rounded-lg hover:bg-white/50 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
                                    onClick={handleCloseApprovalModal}
                                >
                                    <Icon icon="fluent:dismiss-24-regular" width="20" height="20" />
                                </button>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="p-6 space-y-5">
                            {/* Warning Box */}
                            <div className="rounded-xl bg-amber-50 border border-amber-200 p-4">
                                <div className="flex gap-3">
                                    <Icon icon="fluent:warning-24-filled" width="20" height="20" className="text-amber-600 shrink-0 mt-0.5" />
                                    <div>
                                        <h6 className="font-semibold text-amber-900 mb-2 text-sm">Important Information</h6>
                                        <ul className="space-y-1.5 text-sm text-amber-800">
                                            <li className="flex items-start gap-2">
                                                <Icon icon="fluent:subtract-circle-24-regular" width="16" height="16" className="shrink-0 mt-0.5" />
                                                <span>You cannot edit course details after submission</span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <Icon icon="fluent:subtract-circle-24-regular" width="16" height="16" className="shrink-0 mt-0.5" />
                                                <span>Modules and content will be locked</span>
                                            </li>
                                            <li className="flex items-start gap-2">
                                                <Icon icon="fluent:subtract-circle-24-regular" width="16" height="16" className="shrink-0 mt-0.5" />
                                                <span>Please verify all content before proceeding</span>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>

                            {/* Course Info */}
                            <div className="rounded-xl bg-gray-50 border border-gray-200 p-4">
                                <p className="text-sm text-gray-600 mb-2">You are about to {selectedCourse?.course_status === 'pending' ? 'send' : 'resend'} this course for approval:</p>
                                <p className="font-semibold text-gray-900 text-base">"{selectedCourse?.course_name}"</p>
                            </div>

                            <p className="text-sm text-gray-600">Are you sure you want to proceed?</p>
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
                            <button
                                type="button"
                                className="px-4 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-medium text-sm hover:bg-white transition-colors"
                                onClick={handleCloseApprovalModal}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="px-5 py-2.5 rounded-xl bg-[#4F6BFF] hover:bg-[#4358d4] text-white font-medium text-sm shadow-sm transition-colors inline-flex items-center gap-2"
                                onClick={handleSendApprovalConfirm}
                            >
                                {selectedCourse?.course_status === 'pending' ? 'Send for Approval' : 'Resend for Approval'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Course Assignment Modal */}
            <CourseAssignModal
              isOpen={!!assignCourse}
              onClose={(refresh) => {
                setAssignCourse(null);
                if (refresh) fetchCourseList();
              }}
              course={assignCourse}
            />
        </>
    );
}

export default AllCourses

